import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { REQUEST_BOOKING } from '../../util/transaction.default';
import ListingSectionItem from './ListingSectionItem';

// ================ Styles ================ //
const headingStyles = {
  textAlign: { xs: 'center', sm: 'left' },
};
const boxStyles = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  textAlign: {
    md: 'left',
  },
};

/**
 * Displays arrival and departure times for a listing based on public data.
 *
 * @param {object} props - Component props.
 * @param {object} props.publicData - Listing's public data with timing information.
 * @param {Array} props.earliestArrivalTimeOptions - Options for earliest arrival times.
 * @param {Array} props.latestArrivalTimeOptions - Options for latest arrival times.
 * @param {Array} props.latestDepartureTimeOptions - Options for latest departure times.
 * @param {Array} props.options - Options for response times.
 * @returns {JSX.Element|null} The timing information component or null if no data.
 */
const SectionTimingsMaybeComponent = ({
  publicData,
  earliestArrivalTimeOptions,
  latestArrivalTimeOptions,
  latestDepartureTimeOptions,
  options,
}) => {
  const intl = useIntl();

  if (!publicData) {
    return null;
  }

  // Arrival Times
  const earliestArrivalTime = publicData?.earliestArrivalTime;
  const earliestArrivalTimeOption =
    earliestArrivalTime &&
    earliestArrivalTimeOptions.find((option) => option.key === earliestArrivalTime);
  const latestArrivalTime = publicData?.latestArrivalTime;
  const latestArrivalTimeOption =
    latestArrivalTime &&
    latestArrivalTimeOptions.find((option) => option.key === latestArrivalTime);
  const latestDepartureTime = publicData?.latestDepartureTime;
  const latestDepartureTimeOption =
    latestDepartureTime &&
    latestDepartureTimeOptions.find((option) => option.key === latestDepartureTime);

  // If there is no earliest arrival time, use latest arrival time and use the latest arrival time title
  let arrivalTimeTitleID = earliestArrivalTime
    ? 'ListingPage.arrivalTimesTitle'
    : 'ListingPage.latestArrivalTimeTitle';
  const earliestTimeMessage =
    earliestArrivalTime && earliestArrivalTimeOption
      ? intl.formatMessage({ id: earliestArrivalTimeOption.label })
      : '';
  const latestTimeMessage =
    latestArrivalTime && latestArrivalTimeOption
      ? intl.formatMessage({ id: latestArrivalTimeOption.label })
      : '';
  let earliestLatestArrivalTimeString = earliestArrivalTime
    ? `${earliestTimeMessage} - ${latestTimeMessage}`
    : latestTimeMessage;

  // Response Times
  const responseTime = publicData.responseTime;
  const responseTimeOption = options.find((option) => option.key === responseTime);

  return earliestArrivalTimeOption || latestArrivalTimeOption || latestDepartureTimeOption ? (
    <Grid container spacing={2}>
      <ListingSectionItem
        params={{
          size: {
            xs: 12,
            lg: 12,
          },
        }}
      >
        <Stack width="100%">
          <Typography component="h3" mb={2} sx={headingStyles} variant="h4">
            <FormattedMessage id="EditListingWizard.tabLabelLatestArrivalDepartureTime" />
          </Typography>
          <Box>
            <Stack alignItems={{ md: 'flex-start' }} spacing={2}>
              <Stack direction="row" spacing={2}>
                {latestArrivalTimeOption ? (
                  <Box sx={boxStyles}>
                    <Typography component="h4" variant="h6">
                      <FormattedMessage id={arrivalTimeTitleID} />
                    </Typography>
                    <Typography variant="body2">{earliestLatestArrivalTimeString}</Typography>
                  </Box>
                ) : null}

                {latestDepartureTimeOption ? (
                  <Box sx={boxStyles}>
                    <Typography component="h4" variant="h6">
                      <FormattedMessage id="ListingPage.latestDepartureTimeTitle" />
                    </Typography>
                    <Typography variant="body2">
                      {intl.formatMessage({ id: latestDepartureTimeOption.label })}
                    </Typography>
                  </Box>
                ) : null}
              </Stack>

              {responseTimeOption &&
              (!publicData.category || publicData.category === REQUEST_BOOKING) ? (
                <Box>
                  <Typography component="h4" variant="h6">
                    <FormattedMessage id="ListingPage.responseTimeTitle" />
                  </Typography>
                  <Typography variant="body2">
                    {intl.formatMessage({ id: responseTimeOption.label })}
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </ListingSectionItem>
    </Grid>
  ) : null;
};

SectionTimingsMaybeComponent.displayName = 'SectionTimingsMaybe';
const SectionTimingsMaybe = memo(SectionTimingsMaybeComponent);

export default SectionTimingsMaybe;
